import type { ViewerLaunchContextPayload } from './viewerLaunchContextSession';

export const LAUNCH_SELECTION_SESSION_KEY = 'launchSelectionContext';

/** Floor-plan / waitlist fields from GET /viewer/launch-context, mirrored in sessionStorage. */
export interface LaunchSelectionContext {
  propertyFloorplanId?: number;
  unitSpaceId?: number;
  termMonth?: string;
  leaseStartDate?: string;
  leaseTermId?: number;
  leaseStartWindowId?: number;
  installmentPlanId?: number;
  spaceConfigurationId?: number;
  selectedOccupancyTypeId?: number;
  fromWaitlist?: boolean;
  desiredMoveInDate?: string;
  returningApplicant?: boolean;
}

function isPositiveInt(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1;
}

export function buildLaunchSelectionForSession(
  data: ViewerLaunchContextPayload,
): LaunchSelectionContext | null {
  // eslint-disable-next-line no-console
  console.log('[viewer-launch] parsing floor-plan / waitlist launch selection from payload');

  const ctx: LaunchSelectionContext = {};

  if (isPositiveInt(data.propertyFloorplanId)) {
    ctx.propertyFloorplanId = data.propertyFloorplanId;
  }
  if (isPositiveInt(data.unitSpaceId)) {
    ctx.unitSpaceId = data.unitSpaceId;
  }
  if (data.termMonth != null && String(data.termMonth).trim() !== '') {
    ctx.termMonth = String(data.termMonth).trim();
  }
  if (data.leaseStartDate != null && String(data.leaseStartDate).trim() !== '') {
    ctx.leaseStartDate = String(data.leaseStartDate).trim();
  }
  if (isPositiveInt(data.leaseTermId)) {
    ctx.leaseTermId = data.leaseTermId;
  }
  if (isPositiveInt(data.leaseStartWindowId)) {
    ctx.leaseStartWindowId = data.leaseStartWindowId;
  }
  if (isPositiveInt(data.installmentPlanId)) {
    ctx.installmentPlanId = data.installmentPlanId;
  }
  if (isPositiveInt(data.spaceConfigurationId)) {
    ctx.spaceConfigurationId = data.spaceConfigurationId;
  }
  if (isPositiveInt(data.selectedOccupancyTypeId)) {
    ctx.selectedOccupancyTypeId = data.selectedOccupancyTypeId;
  }
  if (data.desiredMoveInDate != null && String(data.desiredMoveInDate).trim() !== '') {
    ctx.desiredMoveInDate = String(data.desiredMoveInDate).trim();
  }
  if (data.fromWaitlist === true || data.fromWaitlist === false) {
    ctx.fromWaitlist = data.fromWaitlist;
  }
  if (data.returningApplicant === true || data.returningApplicant === false) {
    ctx.returningApplicant = data.returningApplicant;
  }

  const result = Object.keys(ctx).length > 0 ? ctx : null;
  // eslint-disable-next-line no-console
  console.log(
    `[viewer-launch] launch selection parsed: ${result ? JSON.stringify(result) : '(none)'}`,
  );
  return result;
}

export function readLaunchSelectionContextFromSession(): LaunchSelectionContext | null {
  const raw = sessionStorage.getItem(LAUNCH_SELECTION_SESSION_KEY);
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    const ctx: LaunchSelectionContext = {};
    const fp = o.propertyFloorplanId;
    if (fp != null && Number.isFinite(Number(fp)) && Number(fp) >= 1) {
      ctx.propertyFloorplanId = Number(fp);
    }
    const us = o.unitSpaceId;
    if (us != null && Number.isFinite(Number(us)) && Number(us) >= 1) {
      ctx.unitSpaceId = Number(us);
    }
    if (typeof o.termMonth === 'string' && o.termMonth.trim() !== '') {
      ctx.termMonth = o.termMonth.trim();
    }
    if (typeof o.leaseStartDate === 'string' && o.leaseStartDate.trim() !== '') {
      ctx.leaseStartDate = o.leaseStartDate.trim();
    }
    if (typeof o.desiredMoveInDate === 'string' && o.desiredMoveInDate.trim() !== '') {
      ctx.desiredMoveInDate = o.desiredMoveInDate.trim();
    }
    const lt = o.leaseTermId;
    if (lt != null && Number.isFinite(Number(lt)) && Number(lt) >= 1) {
      ctx.leaseTermId = Number(lt);
    }
    const lsw = o.leaseStartWindowId;
    if (lsw != null && Number.isFinite(Number(lsw)) && Number(lsw) >= 1) {
      ctx.leaseStartWindowId = Number(lsw);
    }
    const { installmentPlanId } = o;
    if (
      installmentPlanId != null &&
      Number.isFinite(Number(installmentPlanId)) &&
      Number.isInteger(Number(installmentPlanId)) &&
      Number(installmentPlanId) >= 1
    ) {
      ctx.installmentPlanId = Number(installmentPlanId);
    }
    const sc = o.spaceConfigurationId;
    if (sc != null && Number.isFinite(Number(sc)) && Number(sc) >= 1) {
      ctx.spaceConfigurationId = Number(sc);
    }
    const sot = o.selectedOccupancyTypeId;
    if (sot != null && Number.isFinite(Number(sot)) && Number(sot) >= 1) {
      ctx.selectedOccupancyTypeId = Number(sot);
    }
    if (o.fromWaitlist === true || o.fromWaitlist === false) {
      ctx.fromWaitlist = Boolean(o.fromWaitlist);
    }
    if (o.returningApplicant === true || o.returningApplicant === false) {
      ctx.returningApplicant = Boolean(o.returningApplicant);
    }
    return Object.keys(ctx).length > 0 ? ctx : null;
  } catch {
    return null;
  }
}

export function readReturningApplicantFromLaunchSession(): boolean {
  return readLaunchSelectionContextFromSession()?.returningApplicant === true;
}

export function shouldBypassPropertyApplicationRoutes(
  entrataRedirectLink: string,
  ctx: LaunchSelectionContext | null = readLaunchSelectionContextFromSession(),
): boolean {
  if (ctx?.unitSpaceId != null && ctx.unitSpaceId >= 1) return true;
  if (ctx?.fromWaitlist === true) return true;
  return (
    entrataRedirectLink.includes('unit_space[id]') || entrataRedirectLink.includes('from_waitlist')
  );
}

/** Same intent as legacy `validateFloorPlanURL` on entrataRedirectLink, plus flat launch fields. */
export function shouldInitializeFloorPlanFromLaunch(
  entrataRedirectLink: string,
  ctx: LaunchSelectionContext | null = readLaunchSelectionContextFromSession(),
): boolean {
  if (ctx?.propertyFloorplanId != null && ctx.propertyFloorplanId >= 1) return true;
  if (ctx?.unitSpaceId != null && ctx.unitSpaceId >= 1) return true;
  if (ctx?.termMonth) return true;
  if (ctx?.leaseStartDate) return true;
  if (ctx?.leaseTermId != null && ctx.leaseTermId >= 1) return true;
  if (ctx?.leaseStartWindowId != null && ctx.leaseStartWindowId >= 1) return true;
  if (ctx?.fromWaitlist === true) return true;

  if (entrataRedirectLink.includes('lease_start_date')) return true;
  if (entrataRedirectLink.includes('property_floorplan[id]')) return true;
  if (entrataRedirectLink.includes('unit_space[id]')) return true;
  if (entrataRedirectLink.includes('term_month')) return true;
  if (entrataRedirectLink.includes('from_waitlist')) return true;

  return false;
}

export function shouldTreatLaunchAsWaitlist(
  entrataRedirectLink: string,
  ctx: LaunchSelectionContext | null = readLaunchSelectionContextFromSession(),
): boolean {
  if (ctx?.fromWaitlist === true) return true;
  try {
    const pathArr = new URL(entrataRedirectLink).pathname.split('/');
    return pathArr.includes('from_waitlist');
  } catch {
    return false;
  }
}

function pathSegmentAfter(pathArr: string[], key: string): string | undefined {
  const i = pathArr.indexOf(key);
  if (i < 0 || i + 1 >= pathArr.length) return undefined;
  const v = pathArr[i + 1];
  return v === undefined || v === '' ? undefined : v;
}

function parseLaunchSelectionUrl(link: string): URL {
  // API launches can supply the selection as flat launch-context fields without
  // an Entrata redirect link. Use an inert URL in that case so callers can
  // still apply the session-backed selection.
  return new URL(link || 'https://launch-context.invalid');
}

export function extractUnitSelectionFromLinkAndSession(
  link: string,
  ctx: LaunchSelectionContext | null,
): {
  moveInDate: string;
  floorplanId: number;
  unitSpaceId: number;
  termMonth: string;
  leaseTermId: number | undefined;
  spaceConfigurationId: number | undefined;
  leaseStartWindowId: number | undefined;
} {
  const url = parseLaunchSelectionUrl(link);
  const pathArr = url.pathname.split('/');

  const moveInFromQuery = url.searchParams.get('lease_start_date');
  const moveInDate = ctx?.desiredMoveInDate ?? ctx?.leaseStartDate ?? moveInFromQuery ?? '';

  const floorFromPath = pathSegmentAfter(pathArr, 'property_floorplan[id]');
  const floorplanId = Number(
    ctx?.propertyFloorplanId != null && ctx.propertyFloorplanId >= 1
      ? ctx.propertyFloorplanId
      : floorFromPath,
  );

  const unitFromPath = pathSegmentAfter(pathArr, 'unit_space[id]');
  const unitSpaceId = Number(
    ctx?.unitSpaceId != null && ctx.unitSpaceId >= 1 ? ctx.unitSpaceId : unitFromPath,
  );

  const termFromPath = pathSegmentAfter(pathArr, 'term_month') ?? '';
  const termMonth = ctx?.termMonth ?? termFromPath;

  const leaseTermId =
    ctx?.leaseTermId != null && ctx.leaseTermId >= 1 ? ctx.leaseTermId : undefined;

  const spaceConfigurationId =
    ctx?.spaceConfigurationId != null && ctx.spaceConfigurationId >= 1
      ? ctx.spaceConfigurationId
      : undefined;

  const leaseStartWindowId =
    ctx?.leaseStartWindowId != null && ctx.leaseStartWindowId >= 1
      ? ctx.leaseStartWindowId
      : undefined;

  return {
    moveInDate,
    floorplanId,
    unitSpaceId,
    termMonth,
    leaseTermId,
    spaceConfigurationId,
    leaseStartWindowId,
  };
}

export function extractWaitlistSelectionFromLinkAndSession(
  link: string,
  ctx: LaunchSelectionContext | null,
): { waitlistFloorplanId: number; waitlistMoveInDate: string } {
  const url = parseLaunchSelectionUrl(link);
  const pathArr = url.pathname.split('/');
  const fpFromPath = pathSegmentAfter(pathArr, 'property_floorplan_id');
  const waitlistFloorplanId = Number(
    ctx?.propertyFloorplanId != null && ctx.propertyFloorplanId >= 1
      ? ctx.propertyFloorplanId
      : fpFromPath,
  );
  const dateFromPath = pathSegmentAfter(pathArr, 'desired_move_in_date') ?? '';
  const waitlistMoveInDate = ctx?.desiredMoveInDate ?? dateFromPath;
  return { waitlistFloorplanId, waitlistMoveInDate };
}
